// components/PhotoViewer.js — High-res viewer with zoom, annotations, pin-comments,
// and reviewer approve/reject controls.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { deliverFile, isNativeShell, openExternalUrl } from '../utils/download';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const ANNOTATION_COLORS = ['#f45858', '#4f8ef7', '#34c97d', '#f5a623', '#ffffff'];
const ANNOTATION_TOOLS = ['pin', 'arrow', 'circle', 'highlight'];
const REVIEWER_ROLES = ['Reviewer', 'Supervisor', 'Admin', 'R&D', 'R&D Reviewer'];
// Review labels replace the old approve/reject. Order = button order.
const REVIEW_OPTIONS = [
  { key: 'to_discuss', label: 'To discuss', color: '#f5a623' },
  { key: 'feedback_requested', label: 'Feedback requested', color: '#f45858' },
  { key: 'resolved', label: 'Resolved', color: '#34c97d' },
];
const PIN_RADIUS = 18;

export default function PhotoViewer({ photo, photos = [], onClose, onReviewChange }) {
  const { user } = useAuth();
  const toast = useToast();
  const canReview = REVIEWER_ROLES.includes(user?.role);

  const [currentIdx, setCurrentIdx] = useState(Math.max(0, photos.findIndex((p) => p.id === photo?.id)));
  const [zoom, setZoom] = useState(1);
  const [annotating, setAnnotating] = useState(false);
  const [tool, setTool] = useState('pin');
  const [color, setColor] = useState('#f5a623');
  const [annotations, setAnnotations] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const [pendingPin, setPendingPin] = useState(null);  // { x, y } awaiting comment text
  const [pinText, setPinText] = useState('');
  const [activePinId, setActivePinId] = useState(null);
  const [selectedId, setSelectedId] = useState(null); // annotation selected for move/delete
  const moveRef = useRef(null); // { id, startX, startY, original }
  const [reviewBusy, setReviewBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');         // editable review note
  const [reviewState, setReviewState] = useState(null);   // local snapshot to avoid waiting on server roundtrip
  const [downloading, setDownloading] = useState(false);
  const canvasRef = useRef();
  const imgRef = useRef();
  const containerRef = useRef();
  const pinchRef = useRef(null);   // { startDist, startZoom } during a two-finger gesture
  const lastTapRef = useRef(0);    // double-tap-to-zoom detection

  const currentPhoto = photos.length > 0 ? photos[currentIdx] : photo;
  const serverBase = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    setReviewState({
      status: currentPhoto?.review_status || 'pending',
      note: currentPhoto?.review_note || '',
    });
    setNoteDraft(currentPhoto?.review_note || '');
  }, [currentPhoto?.id, currentPhoto?.review_status, currentPhoto?.review_note]);

  // Pins (single-shape annotations of type 'pin') get a sequential label number.
  const pinsWithIndex = (() => {
    let n = 0;
    return annotations.map((ann) => {
      const shapes = parseShapes(ann.data);
      if (shapes.length === 1 && shapes[0].type === 'pin') {
        n += 1;
        return { ...ann, pinNumber: n, pinShape: shapes[0] };
      }
      return { ...ann, pinNumber: null };
    });
  })();
  const pins = pinsWithIndex.filter((a) => a.pinNumber != null);

  const loadAnnotations = useCallback(async () => {
    if (!currentPhoto?.id) return;
    try {
      const { data } = await api.get(`/api/photos/${currentPhoto.id}/annotations`);
      setAnnotations(data);
    } catch {}
  }, [currentPhoto?.id]);

  useEffect(() => { loadAnnotations(); }, [loadAnnotations]);

  const drawShapes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let pinNum = 0;
    for (const ann of annotations) {
      const shapes = parseShapes(ann.data);
      for (const shape of shapes) {
        ctx.strokeStyle = shape.color || '#f5a623';
        ctx.fillStyle = shape.color || '#f5a623';
        ctx.lineWidth = 3;

        if (shape.type === 'pin') {
          pinNum += 1;
          const isActive = ann.id === activePinId;
          ctx.beginPath();
          ctx.arc(shape.x, shape.y, PIN_RADIUS * (isActive ? 1.25 : 1), 0, Math.PI * 2);
          ctx.fillStyle = shape.color || '#f5a623';
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#000';
          ctx.stroke();
          ctx.fillStyle = '#000';
          ctx.font = `bold ${PIN_RADIUS}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(pinNum), shape.x, shape.y + 1);
        } else if (shape.type === 'arrow') {
          ctx.beginPath();
          ctx.moveTo(shape.x1, shape.y1);
          ctx.lineTo(shape.x2, shape.y2);
          ctx.stroke();
          const angle = Math.atan2(shape.y2 - shape.y1, shape.x2 - shape.x1);
          ctx.beginPath();
          ctx.moveTo(shape.x2, shape.y2);
          ctx.lineTo(shape.x2 - 15 * Math.cos(angle - 0.4), shape.y2 - 15 * Math.sin(angle - 0.4));
          ctx.lineTo(shape.x2 - 15 * Math.cos(angle + 0.4), shape.y2 - 15 * Math.sin(angle + 0.4));
          ctx.closePath();
          ctx.fillStyle = shape.color || '#f5a623';
          ctx.fill();
        } else if (shape.type === 'circle') {
          ctx.beginPath();
          ctx.arc(shape.cx, shape.cy, shape.r, 0, Math.PI * 2);
          ctx.stroke();
        } else if (shape.type === 'highlight') {
          ctx.fillStyle = (shape.color || '#f5a623') + '44';
          ctx.fillRect(shape.x, shape.y, shape.w, shape.h);
        }

        // Selection outline so the user sees what they're about to move/delete.
        if (ann.id === selectedId) {
          ctx.save();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 4]);
          const b = shapeBounds(shape);
          if (b) ctx.strokeRect(b.x - 8, b.y - 8, b.w + 16, b.h + 16);
          ctx.restore();
        }
      }
    }
  }, [annotations, activePinId, selectedId]);

  useEffect(drawShapes, [drawShapes]);

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // touchend has an empty `touches` list — the lifted finger is in changedTouches.
    const point = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]) || e;
    return {
      x: (point.clientX - rect.left) * (canvas.width / rect.width),
      y: (point.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const touchDistance = (e) =>
    Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);

  const handlePointerDown = (e) => {
    // Two fingers = pinch zoom (tablet). Cancel any drawing/move in progress.
    if (e.touches && e.touches.length === 2) {
      pinchRef.current = { startDist: touchDistance(e), startZoom: zoom };
      moveRef.current = null;
      setDrawing(null);
      return;
    }
    const pos = getCanvasPos(e);
    if (!annotating) {
      // Even when not annotating, allow clicking on a pin to focus its comment.
      const clicked = pins.find((p) => Math.hypot(pos.x - p.pinShape.x, pos.y - p.pinShape.y) <= PIN_RADIUS * 1.5);
      if (clicked) setActivePinId(clicked.id);
      return;
    }
    // In annotate mode, tapping an existing shape selects it (and starts a move)
    // instead of drawing a new one — works for pins, arrows, circles, highlights.
    const hit = hitTestAnnotation(annotations, pos);
    if (hit) {
      setSelectedId(hit.id);
      if (hit.shape.type === 'pin') setActivePinId(hit.id);
      moveRef.current = { id: hit.id, startX: pos.x, startY: pos.y, original: hit.shape, moved: false };
      return;
    }
    setSelectedId(null);
    if (tool === 'pin') {
      setPendingPin({ x: pos.x, y: pos.y });
      setPinText('');
      return;
    }
    setDrawing({ startX: pos.x, startY: pos.y, currentX: pos.x, currentY: pos.y });
  };

  const handlePointerMove = (e) => {
    // Pinch in progress: scale zoom by finger distance.
    if (pinchRef.current && e.touches && e.touches.length === 2) {
      const factor = touchDistance(e) / pinchRef.current.startDist;
      setZoom(Math.min(4, Math.max(1, pinchRef.current.startZoom * factor)));
      return;
    }
    // Dragging a selected shape to a new position.
    if (moveRef.current && annotating) {
      const pos = getCanvasPos(e);
      const m = moveRef.current;
      const dx = pos.x - m.startX, dy = pos.y - m.startY;
      if (Math.hypot(dx, dy) > 2) m.moved = true;
      const moved = translateShape(m.original, dx, dy);
      setAnnotations((curr) => curr.map((a) => (a.id === m.id ? { ...a, data: [moved] } : a)));
      return;
    }
    if (!drawing || !annotating || tool === 'pin') return;
    const pos = getCanvasPos(e);
    setDrawing((d) => ({ ...d, currentX: pos.x, currentY: pos.y }));
    drawShapes();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    if (tool === 'arrow') {
      ctx.beginPath();
      ctx.moveTo(drawing.startX, drawing.startY);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (tool === 'circle') {
      const r = Math.hypot(pos.x - drawing.startX, pos.y - drawing.startY);
      ctx.beginPath();
      ctx.arc(drawing.startX, drawing.startY, r, 0, Math.PI * 2);
      ctx.stroke();
    } else if (tool === 'highlight') {
      ctx.fillStyle = color + '44';
      ctx.fillRect(drawing.startX, drawing.startY, pos.x - drawing.startX, pos.y - drawing.startY);
    }
  };

  const handlePointerUp = async (e) => {
    // End of a pinch: wait until all fingers are lifted before treating touches
    // as taps/draws again, so releasing one finger doesn't draw a stray shape.
    if (pinchRef.current) {
      if (!e.touches || e.touches.length === 0) pinchRef.current = null;
      lastTapRef.current = 0;
      return;
    }
    // Double-tap toggles zoom (viewing mode only — annotating needs precise taps).
    if (e.changedTouches && !annotating && !drawing && !moveRef.current) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        setZoom((z) => (z > 1 ? 1 : 2));
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    }
    // Finish a move: persist the new position (or drop it if it was just a tap).
    if (moveRef.current && annotating) {
      const m = moveRef.current;
      moveRef.current = null;
      if (m.moved) {
        const ann = annotations.find((a) => a.id === m.id);
        const newShape = ann ? parseShapes(ann.data)[0] : null;
        if (newShape) {
          try { await api.put(`/api/annotations/${m.id}`, { data: [newShape] }); }
          catch { loadAnnotations(); toast?.error?.('Could not move annotation'); }
        }
      }
      return;
    }
    if (!drawing || !annotating || tool === 'pin') return;
    const pos = getCanvasPos(e);
    let shape;
    if (tool === 'arrow') {
      shape = { type: 'arrow', x1: drawing.startX, y1: drawing.startY, x2: pos.x, y2: pos.y, color };
    } else if (tool === 'circle') {
      const r = Math.hypot(pos.x - drawing.startX, pos.y - drawing.startY);
      shape = { type: 'circle', cx: drawing.startX, cy: drawing.startY, r, color };
    } else {
      shape = { type: 'highlight', x: drawing.startX, y: drawing.startY, w: pos.x - drawing.startX, h: pos.y - drawing.startY, color };
    }
    setDrawing(null);
    try {
      await api.post(`/api/photos/${currentPhoto.id}/annotations`, { data: [shape] });
      loadAnnotations();
    } catch {}
  };

  const confirmPin = async () => {
    if (!pendingPin) return;
    const text = pinText.trim();
    if (!text) {
      setPendingPin(null);
      return;
    }
    const shape = { type: 'pin', x: pendingPin.x, y: pendingPin.y, comment: text, color };
    try {
      await api.post(`/api/photos/${currentPhoto.id}/annotations`, { data: [shape] });
      await loadAnnotations();
    } catch (err) {
      toast?.error?.('Could not save pin');
    } finally {
      setPendingPin(null);
      setPinText('');
    }
  };

  const deleteAnnotation = async (annId) => {
    try {
      await api.delete(`/api/annotations/${annId}`);
      setAnnotations((curr) => curr.filter((a) => a.id !== annId));
      if (activePinId === annId) setActivePinId(null);
      if (selectedId === annId) setSelectedId(null);
    } catch {
      toast?.error?.('Could not remove annotation');
    }
  };

  // Apply a label (to_discuss / feedback_requested / resolved). Passing the same
  // label again just re-saves the note. Resolved/cleared photos leave the queue.
  const submitReview = async (status) => {
    if (!canReview) return;
    const note = noteDraft.trim() || null;
    setReviewBusy(true);
    try {
      await api.post(`/api/photos/${currentPhoto.id}/review`, { status, note });
      setReviewState({ status, note });
      onReviewChange?.(currentPhoto.id, status);
      const opt = REVIEW_OPTIONS.find((o) => o.key === status);
      toast?.success?.(opt ? `${opt.label} ✓` : 'Saved');
    } catch {
      toast?.error?.('Review save failed');
    } finally {
      setReviewBusy(false);
    }
  };

  // Persist a note edit without changing the label (once a label exists).
  const saveNote = () => {
    if (!canReview) return;
    const status = reviewState?.status;
    if (!status || status === 'pending') return;       // no label yet → nothing to attach to
    if ((noteDraft.trim() || '') === (reviewState?.note || '')) return;
    submitReview(status);
  };

  const imgSrc = currentPhoto?.server_path
    ? `${serverBase}${currentPhoto.server_path}`
    : currentPhoto?.local_data || '';

  // Download the real photo file via the API. Right-click "save image" grabs the
  // transparent annotation canvas that covers the <img>, giving white/black files.
  const downloadPhoto = async () => {
    if (!currentPhoto?.id || downloading) return;
    setDownloading(true);
    try {
      // The shell's WebView drops blob downloads, so there we ask for a signed URL and let
      // the system browser save it. See utils/download.js.
      if (isNativeShell()) {
        const linkRes = await api.get(`/api/photos/${currentPhoto.id}/download?link=1`, { timeout: 60000 });
        if (!linkRes.data?.url) throw new Error('no download url');
        openExternalUrl(linkRes.data.url);
        toast?.success?.('Saving in your browser');
        return;
      }

      const res = await api.get(`/api/photos/${currentPhoto.id}/download`, { responseType: 'blob', timeout: 60000 });
      const disposition = res.headers?.['content-disposition'] || '';
      const nameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = nameMatch ? nameMatch[1] : `${(currentPhoto.step_name || 'photo').replace(/[^\w-]+/g, '_')}.jpg`;
      const outcome = await deliverFile(res.data, filename);
      if (outcome === 'unsupported') toast?.error?.('This device cannot save the file here');
    } catch {
      toast?.error?.('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const reviewBadge = (() => {
    const opt = REVIEW_OPTIONS.find((o) => o.key === reviewState?.status);
    if (!opt) return { label: 'Unlabelled', bg: 'rgba(120,120,120,0.85)' };
    return { label: opt.label, bg: opt.color };
  })();

  return (
    <div className="photo-viewer-overlay" ref={containerRef}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-ghost btn-icon" onClick={onClose} style={{ color: 'white' }}>✕</button>
          <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
            {currentPhoto?.step_name || currentPhoto?.step_label}
            {photos.length > 1 && ` (${currentIdx + 1}/${photos.length})`}
          </span>
          <span style={{
            padding: '2px 10px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700,
            color: 'white', background: reviewBadge.bg,
          }}>{reviewBadge.label}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {canReview && REVIEW_OPTIONS.map((o) => (
            <button
              key={o.key}
              disabled={reviewBusy}
              onClick={() => submitReview(o.key)}
              title={`Label: ${o.label}`}
              style={reviewBtn(reviewState?.status === o.key ? o.color : 'rgba(255,255,255,0.1)')}
            >{o.label}</button>
          ))}
          <button className="btn btn-ghost btn-sm" style={{ color: annotating ? 'var(--accent-primary)' : 'rgba(255,255,255,0.7)' }}
            onClick={() => setAnnotating(!annotating)}>✏ Annotate</button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'rgba(255,255,255,0.7)' }}
            disabled={downloading} onClick={downloadPhoto} title="Download this photo">
            {downloading ? '…' : '⤓ Save'}
          </button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'rgba(255,255,255,0.7)' }}
            onClick={() => setZoom((z) => Math.min(z + 0.5, 4))}>＋</button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'rgba(255,255,255,0.7)' }}
            onClick={() => setZoom((z) => Math.max(z - 0.5, 1))}>－</button>
          {zoom > 1 && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'rgba(255,255,255,0.7)' }} onClick={() => setZoom(1)}>Reset</button>
          )}
        </div>
      </div>

      {/* Review note — attached to the photo's label, shown in the Friday report */}
      {canReview && (
        <div style={toolbarStyle}>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>Note</span>
          <input
            placeholder="Add a note for this photo (optional)"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={saveNote}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
            style={{
              flex: 1, padding: '6px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(0,0,0,0.4)', color: 'white', fontSize: '0.85rem',
            }}
          />
        </div>
      )}

      {/* Annotation toolbar */}
      {annotating && (
        <div style={toolbarStyle}>
          {ANNOTATION_TOOLS.map((t) => (
            <button key={t} onClick={() => setTool(t)} className="btn btn-sm"
              style={{
                background: tool === t ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)',
                color: 'white', border: 'none', textTransform: 'capitalize', padding: '4px 10px',
              }}>
              {t === 'pin' ? '📍' : t === 'arrow' ? '→' : t === 'circle' ? '○' : '▭'} {t}
            </button>
          ))}
          <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
          {ANNOTATION_COLORS.map((c) => (
            <div key={c} onClick={() => setColor(c)} style={{
              width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer',
              border: color === c ? '3px solid white' : '2px solid transparent',
            }} />
          ))}
          <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
          {selectedId ? (
            <button onClick={() => deleteAnnotation(selectedId)} style={reviewBtn('#f45858')}>🗑 Delete selected</button>
          ) : (
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Tap a mark to move or delete it</span>
          )}
        </div>
      )}

      {/* Image + Canvas */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <div style={{ position: 'relative', transform: `scale(${zoom})`, transformOrigin: 'center center', transition: 'transform 0.2s ease' }}>
          <img
            ref={imgRef}
            src={imgSrc}
            alt={currentPhoto?.step_name}
            className="photo-viewer-img"
            style={{ maxWidth: '100vw', maxHeight: 'calc(100vh - 220px)', display: 'block' }}
            onLoad={() => {
              if (canvasRef.current && imgRef.current) {
                canvasRef.current.width = imgRef.current.naturalWidth;
                canvasRef.current.height = imgRef.current.naturalHeight;
                drawShapes();
              }
            }}
          />
          <canvas
            ref={canvasRef}
            className="annotation-canvas"
            style={{ pointerEvents: 'all', position: 'absolute', inset: 0, cursor: annotating ? 'crosshair' : 'pointer', touchAction: 'manipulation' }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
          />
        </div>
      </div>

      {/* Pin comment modal */}
      {pendingPin && (
        <div style={pinModalStyle} onClick={() => setPendingPin(null)}>
          <div onClick={(e) => e.stopPropagation()} style={pinModalInnerStyle}>
            <h4 style={{ margin: 0 }}>📍 Add comment</h4>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '4px 0 12px' }}>
              Describe what you see at this spot.
            </p>
            <textarea
              autoFocus
              value={pinText}
              onChange={(e) => setPinText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) confirmPin(); }}
              placeholder="e.g. Scratch on left armrest"
              style={{
                width: '100%', minHeight: 80, padding: 10, borderRadius: 6,
                border: '1px solid var(--border-subtle)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', fontSize: '0.875rem', resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setPendingPin(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={confirmPin} disabled={!pinText.trim()}>
                Save pin (⌘+Enter)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pin comment list */}
      {pins.length > 0 && (
        <div style={pinListStyle}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
            {pins.length} pin{pins.length === 1 ? '' : 's'} · click to focus
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {pins.map((p) => (
              <button
                key={p.id}
                onClick={() => setActivePinId(p.id)}
                title={p.pinShape.comment}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 14,
                  border: activePinId === p.id ? '2px solid white' : '1px solid rgba(255,255,255,0.2)',
                  background: p.pinShape.color || '#f5a623',
                  color: '#000', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  maxWidth: 280,
                }}
              >
                <span>{p.pinNumber}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.pinShape.comment || '(no comment)'}
                </span>
              </button>
            ))}
          </div>
          {activePinId && (() => {
            const ap = pins.find((p) => p.id === activePinId);
            if (!ap) return null;
            return (
              <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>
                  <strong>#{ap.pinNumber}</strong> {ap.pinShape.comment} · {ap.author_name ? `by ${ap.author_name}` : ''}
                </span>
                <button onClick={() => deleteAnnotation(ap.id)} style={{
                  background: 'transparent', border: 'none', color: '#f45858',
                  cursor: 'pointer', fontSize: '0.75rem',
                }}>Remove</button>
              </div>
            );
          })()}
        </div>
      )}

      {/* Navigation arrows */}
      {photos.length > 1 && (
        <>
          <button onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))} disabled={currentIdx === 0} style={navArrowStyle('left')}>‹</button>
          <button onClick={() => setCurrentIdx((i) => Math.min(photos.length - 1, i + 1))} disabled={currentIdx === photos.length - 1} style={navArrowStyle('right')}>›</button>
        </>
      )}

      {/* Photo metadata */}
      <div style={metaStyle}>
        <span>By: {currentPhoto?.taken_by_name}</span>
        <span>{currentPhoto?.taken_at ? new Date(currentPhoto.taken_at).toLocaleString() : ''}</span>
        {currentPhoto?.width && <span>{currentPhoto.width}×{currentPhoto.height}px</span>}
        {reviewState?.note && <span style={{ color: 'rgba(255,220,180,0.9)' }}>Note: {reviewState.note}</span>}
        <div className="sync-dot" style={{
          background: currentPhoto?.sync_status === 'synced' ? 'var(--accent-success)' : 'var(--accent-warning)',
          alignSelf: 'center',
        }} />
      </div>
    </div>
  );
}

// ── helpers / styles ────────────────────────────────────────────────────────
function parseShapes(data) {
  if (Array.isArray(data)) return data;
  try { const parsed = JSON.parse(data); return Array.isArray(parsed) ? parsed : []; }
  catch { return []; }
}

const HIT_TOL = 14;

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - x1) * dx + (py - y1) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function shapeHit(shape, pos) {
  if (!shape) return false;
  if (shape.type === 'pin') return Math.hypot(pos.x - shape.x, pos.y - shape.y) <= 18 * 1.5;
  if (shape.type === 'arrow') return distToSegment(pos.x, pos.y, shape.x1, shape.y1, shape.x2, shape.y2) <= HIT_TOL;
  if (shape.type === 'circle') return Math.abs(Math.hypot(pos.x - shape.cx, pos.y - shape.cy) - shape.r) <= HIT_TOL;
  if (shape.type === 'highlight') {
    const x = Math.min(shape.x, shape.x + shape.w), y = Math.min(shape.y, shape.y + shape.h);
    const w = Math.abs(shape.w), h = Math.abs(shape.h);
    return pos.x >= x - HIT_TOL && pos.x <= x + w + HIT_TOL && pos.y >= y - HIT_TOL && pos.y <= y + h + HIT_TOL;
  }
  return false;
}

// Topmost (last-drawn) annotation under the pointer, if any.
function hitTestAnnotation(annotations, pos) {
  for (let i = annotations.length - 1; i >= 0; i--) {
    const shape = parseShapes(annotations[i].data)[0];
    if (shapeHit(shape, pos)) return { id: annotations[i].id, shape };
  }
  return null;
}

function shapeBounds(shape) {
  if (!shape) return null;
  if (shape.type === 'pin') return { x: shape.x - 18, y: shape.y - 18, w: 36, h: 36 };
  if (shape.type === 'arrow') {
    const x = Math.min(shape.x1, shape.x2), y = Math.min(shape.y1, shape.y2);
    return { x, y, w: Math.abs(shape.x2 - shape.x1), h: Math.abs(shape.y2 - shape.y1) };
  }
  if (shape.type === 'circle') return { x: shape.cx - shape.r, y: shape.cy - shape.r, w: shape.r * 2, h: shape.r * 2 };
  if (shape.type === 'highlight') {
    return { x: Math.min(shape.x, shape.x + shape.w), y: Math.min(shape.y, shape.y + shape.h), w: Math.abs(shape.w), h: Math.abs(shape.h) };
  }
  return null;
}

function translateShape(shape, dx, dy) {
  const s = { ...shape };
  if (s.type === 'pin') { s.x += dx; s.y += dy; }
  else if (s.type === 'arrow') { s.x1 += dx; s.y1 += dy; s.x2 += dx; s.y2 += dy; }
  else if (s.type === 'circle') { s.cx += dx; s.cy += dy; }
  else if (s.type === 'highlight') { s.x += dx; s.y += dy; }
  return s;
}

const headerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '10px 16px', background: 'rgba(0,0,0,0.8)',
  borderBottom: '1px solid rgba(255,255,255,0.1)', gap: 12, flexWrap: 'wrap',
};

const toolbarStyle = {
  display: 'flex', gap: 8, padding: '8px 16px', alignItems: 'center',
  background: 'rgba(0,0,0,0.8)', borderBottom: '1px solid rgba(255,255,255,0.1)',
};

const reviewBtn = (bg) => ({
  background: bg, border: 'none', color: 'white', padding: '6px 12px',
  borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
});

const pinModalStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16,
};

const pinModalInnerStyle = {
  background: 'var(--bg-primary)', borderRadius: 8, padding: 20,
  maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};

const pinListStyle = {
  padding: '10px 16px', background: 'rgba(0,0,0,0.85)',
  borderTop: '1px solid rgba(255,255,255,0.1)',
};

const metaStyle = {
  padding: '8px 16px', background: 'rgba(0,0,0,0.8)',
  display: 'flex', gap: 16, fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', flexWrap: 'wrap',
};

const navArrowStyle = (side) => ({
  position: 'absolute', [side]: 10, top: '50%', transform: 'translateY(-50%)',
  background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white',
  borderRadius: '50%', width: 44, height: 44, fontSize: '1.25rem',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
});
